import { Box, DialogContent, DialogContentText, TextField } from '@mui/material';
import React from 'react';
import {
  auth,
  finishMfaSignIn,
  getRecaptchaVerifier,
  retrieveMultiFactorResolver,
  setVerificationId
} from '../../../lib/google/firebase';
import { FirebaseError } from '@firebase/util';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { MetadataError } from '@ep/error-handling';
import { log } from '../../../util/log';
import { generateUuidV4 } from '../../../util/uuid';
import PrimaryButton from '../../shared/button/PrimaryButton';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import {
  MultiFactorResolver,
  PhoneAuthProvider,
  PhoneInfoOptions,
  RecaptchaVerifier
} from 'firebase/auth';
import { BodyText, Subheading } from '../../shared/typography/PrimaryTypography';
import { MFA_TIMEOUT_MS } from '../../../env';
import { trackEvent } from '../../../lib/app/analytics/ga';

const default_mfa_error = 'Unknown Error Occured';

/**
 * A modal window to allow user to enter a verification code sent via sms for MFA authentication.
 * @param {number} verify_timeout Countdown value to timeout when the user resends a verification code.
 * @param {void} resetVerifyTimeout Function to Reset the timeout when the user clicks resend.
 * @param {void} setAuthenticated Function to set the authenticated state of the user.
 * @param {void} closeLogin Function to trigger on closing of the modal window.
 * @returns {JSX.Element}
 */
const VerifySmsMfa = ({
  verify_timeout,
  resetVerifyTimeout,
  setAuthenticated,
  closeLogin
}: {
  verify_timeout: number;
  setAuthenticated: (authenticated: boolean) => void;
  closeLogin: () => void;
  resetVerifyTimeout: () => void;
}): JSX.Element => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const [code, setCode] = React.useState<string>('');
  const [validCode, setValidCode] = React.useState<boolean>(false);
  const [verifyLoad, setVerifyLoad] = React.useState<boolean>(false);
  const [loginError, setLoginError] = React.useState<boolean>(false);
  const [loginErrorMessage, setLoginErrorMessage] = React.useState<string>(default_mfa_error);
  const navigate: NavigateFunction = useNavigate();

  // Handles resending of verification code to user if code was not recieved.
  const resendMfaCode = async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    event.preventDefault();
    const resolver: MultiFactorResolver | null = retrieveMultiFactorResolver();
    if (resolver) {
      const phone_info_options: PhoneInfoOptions = {
        multiFactorHint: resolver.hints[0],
        session: resolver.session
      };

      const phoneAuthProvider: PhoneAuthProvider = new PhoneAuthProvider(auth);
      const recaptcha_verifier: RecaptchaVerifier | null = getRecaptchaVerifier();
      if (recaptcha_verifier)
        setVerificationId(
          await phoneAuthProvider.verifyPhoneNumber(phone_info_options, recaptcha_verifier)
        );
      resetVerifyTimeout();
    }
  };

  // Handles setting of verification code state.
  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setCode(event.target.value);
  };

  // Handles setting of MFA error text.
  const handleMfaError = (state: boolean, message: string): void => {
    setLoginError(state);
    setLoginErrorMessage(message);
  };

  // Handles closing and resetting of sign in state to initial login state.
  const handleClose = (): void => {
    closeLogin();
    setVerifyLoad(false);
  };

  // Handles of closing the MFA verification before it times out to force user to restart process
  React.useEffect(() => {
    const timeout_id: NodeJS.Timeout = setTimeout(async () => {
      handleClose();
    }, MFA_TIMEOUT_MS);
    return () => clearTimeout(timeout_id);
  }, []);

  // Handles verification of user supplied code sent via email.
  const handleMfaVerification = async (
    event: React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    event.preventDefault();
    try {
      setVerifyLoad(true);
      await finishMfaSignIn(code);
      setCode('');
      setAuthenticated(true);
      // Send user login event to Google Analytics.
      trackEvent(
        'user_login',
        {
          syntiro_user_id: auth.currentUser?.uid,
          syntiro_user_email: auth.currentUser?.email
        },
        false
      );
      navigate('/');
      handleClose();
      if (loginError === true) handleMfaError(false, default_mfa_error);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      if ((err as FirebaseError).code == 'auth/invalid-verification-code') {
        handleMfaError(true, 'Incorrect verification code, please try again!');
      } else if ((err as FirebaseError).code == 'auth/too-many-requests') {
        handleMfaError(true, 'Too many login requests, please try again later');
      } else if ((err as FirebaseError).code == 'auth/quota-exceeded') {
        handleMfaError(true, 'Too many verification requests, please try again later');
      } else if ((err as FirebaseError).code == 'auth/code-expired') {
        handleMfaError(true, 'Code expired, please click resend for a new code');
      } else {
        log(
          'error',
          new MetadataError(
            err instanceof Error
              ? err.message
              : 'Error: VerifySmsMfa failed on an unknown error while calling handleMfaVerification.',
            err instanceof FirebaseError
              ? {
                  firebase_error_code: err.code,
                  code: code
                }
              : {
                  code: code
                },
            tracking_id
          )
        );
        setFeedbackData({
          message: `An unknown error has occured. Tracking ID: ${tracking_id}`,
          state: true,
          type: 'error'
        });
      }
      setVerifyLoad(false);
    }
  };

  // Handles the debounce of the user code input and handles the validity of its input.
  React.useEffect(() => {
    setTimeout(() => {
      setValidCode(code.length > 0);
    }, 250);
    return () => {
      null;
    };
  }, [code]);

  return (
    <Box>
      <Subheading>SMS Two-Factor Verification</Subheading>
      <BodyText sx={{ px: 3, py: 1 }}>
        Verification code has been sent to your registered number, please enter it below.
      </BodyText>
      <form>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="text"
            label="Verification Code"
            type="text"
            fullWidth
            variant="outlined"
            onChange={handleCodeChange}
            sx={{ my: 2 }}
          />
          {loginError && (
            <DialogContentText sx={{ color: 'red' }}>{loginErrorMessage}</DialogContentText>
          )}
        </DialogContent>
        <Box justifyContent={'space-around'} display="flex">
          <PrimaryButton
            disabled={verify_timeout !== 0}
            data-testid="resend-button"
            onClick={resendMfaCode}
            sx={{ minWidth: '150px' }}
          >
            Resend Code{verify_timeout > 0 ? `: ${verify_timeout}` : ''}
          </PrimaryButton>
          <PrimaryButton
            id="verify-button"
            loading={verifyLoad}
            data-testid="verify-button"
            onClick={handleMfaVerification}
            sx={{ minWidth: '150px' }}
            disabled={!validCode}
            type="submit"
          >
            Verify
          </PrimaryButton>
        </Box>
      </form>
    </Box>
  );
};

export default VerifySmsMfa;
